// Phase 9A.6.1 — Public signal-card watchlist DB helpers.
//
// Thin Supabase wrappers extracted from `usePublicSignalCardWatchlist`
// for maintainability. NO React, NO state, NO clock. Each helper is
// a focused single-purpose call against `signal_card_watchlist`. The
// hook stays the single entry point; nothing else in the app should
// import these helpers.
//
// Trust contract:
//   - Reads ONLY from `signal_card_watchlist`. Does NOT touch
//     signal_card_grades, daily_intelligence_ledger,
//     fixture_market_closing_lines, fixture_market_odds_history,
//     provider_ingestion_runs, fair_price_predictions, input_snapshot,
//     explanation, evidence_payload, or raw_payload.
//   - Returns plain `{ ok, error?, ... }` shapes. No throws.
//   - Idempotent save: surfaces a `duplicate` flag so the hook can
//     keep its set consistent.

import type { SupabaseClient } from "@supabase/supabase-js";

import type { Database } from "../db/types.ts";

export interface SavedSignalRow {
  signalCardId: string;
  privateNote: string | null;
  privateTags: string[];
}

export interface FetchSavedRowsResult {
  ok: boolean;
  rows: SavedSignalRow[];
  error?: string;
}

export async function fetchSavedRows(
  supabase: SupabaseClient<Database>,
): Promise<FetchSavedRowsResult> {
  const r = await supabase
    .from("signal_card_watchlist")
    .select("signal_card_id, private_note, private_tags");
  if (r.error) {
    return { ok: false, rows: [], error: r.error.message };
  }
  const rows: SavedSignalRow[] = [];
  for (const row of r.data ?? []) {
    if (typeof row.signal_card_id !== "string" || row.signal_card_id.length === 0) continue;
    const note = (row as { private_note?: string | null }).private_note;
    const tagsRaw = (row as { private_tags?: string[] | null }).private_tags;
    const tags = Array.isArray(tagsRaw)
      ? tagsRaw.filter((t): t is string => typeof t === "string")
      : [];
    rows.push({
      signalCardId: row.signal_card_id,
      privateNote: typeof note === "string" ? note : null,
      privateTags: tags,
    });
  }
  return { ok: true, rows };
}

export async function updateSavedSignalTags(
  supabase: SupabaseClient<Database>,
  userId: string,
  signalCardId: string,
  tags: string[],
): Promise<MutationResult> {
  const upd = await supabase
    .from("signal_card_watchlist")
    .update({ private_tags: tags })
    .eq("user_id", userId)
    .eq("signal_card_id", signalCardId);
  if (upd.error !== null) return { ok: false, error: upd.error.message };
  return { ok: true };
}

export interface MutationResult {
  ok: boolean;
  duplicate?: boolean;
  error?: string;
}

export async function insertSavedSignalCard(
  supabase: SupabaseClient<Database>,
  userId: string,
  signalCardId: string,
): Promise<MutationResult> {
  const ins = await supabase
    .from("signal_card_watchlist")
    .insert({ user_id: userId, signal_card_id: signalCardId });
  if (ins.error === null) return { ok: true };
  // Treat unique-violation (23505) as idempotent success — the row
  // already exists for this user.
  const isDuplicate = ins.error.code === "23505" || ins.error.message.includes("duplicate key");
  if (isDuplicate) return { ok: true, duplicate: true };
  return { ok: false, error: ins.error.message };
}

export async function deleteSavedSignalCard(
  supabase: SupabaseClient<Database>,
  userId: string,
  signalCardId: string,
): Promise<MutationResult> {
  const del = await supabase
    .from("signal_card_watchlist")
    .delete()
    .eq("user_id", userId)
    .eq("signal_card_id", signalCardId);
  if (del.error !== null) return { ok: false, error: del.error.message };
  return { ok: true };
}

export async function updateSavedSignalNote(
  supabase: SupabaseClient<Database>,
  userId: string,
  signalCardId: string,
  noteValue: string | null,
): Promise<MutationResult> {
  const upd = await supabase
    .from("signal_card_watchlist")
    .update({ private_note: noteValue })
    .eq("user_id", userId)
    .eq("signal_card_id", signalCardId);
  if (upd.error !== null) return { ok: false, error: upd.error.message };
  return { ok: true };
}
